// Breadcrumb (formato Sentry) e ring buffer. Specchio di `Breadcrumb` /
// `BreadcrumbBuffer` (dart). I `data` vengono scrubati alla costruzione.

import { scrub } from './scrubber'
import type { EventPayload } from './types'

export interface BreadcrumbInput {
  message?: string
  category?: string
  /// Tipo Sentry: `default`, `query`, `http`, `user`, `navigation`.
  type?: string
  /// Livello: `info`, `debug`, `warning`, `error`.
  level?: string
  data?: Record<string, unknown>
}

export class Breadcrumb {
  readonly timestamp: string
  readonly message: string | undefined
  readonly category: string | undefined
  readonly type: string
  readonly level: string
  readonly data: Record<string, unknown> | undefined

  constructor(input: BreadcrumbInput) {
    this.timestamp = new Date().toISOString()
    this.message = input.message
    this.category = input.category
    this.type = input.type ?? 'default'
    this.level = input.level ?? 'info'
    this.data =
      input.data && Object.keys(input.data).length > 0 ? scrub(input.data) : undefined
  }

  toJSON(): EventPayload {
    const json: EventPayload = {
      timestamp: this.timestamp,
      type: this.type,
      level: this.level,
    }
    if (this.category !== undefined) json['category'] = this.category
    if (this.message !== undefined) json['message'] = this.message
    if (this.data !== undefined) json['data'] = this.data
    return json
  }
}

/// Ring buffer: tiene gli ultimi [maxSize], scarta i più vecchi.
export class BreadcrumbBuffer {
  private items: Breadcrumb[] = []

  constructor(private readonly maxSize: number = 50) {}

  get length(): number {
    return this.items.length
  }

  add(crumb: Breadcrumb): void {
    if (this.maxSize <= 0) return
    this.items.push(crumb)
    while (this.items.length > this.maxSize) this.items.shift()
  }

  clear(): void {
    this.items = []
  }

  toList(): Breadcrumb[] {
    return [...this.items]
  }

  /// Serializza in `{values: [...]}` (formato Sentry), o `null` se vuoto.
  toJSON(): EventPayload | null {
    if (this.items.length === 0) return null
    return { values: this.items.map((c) => c.toJSON()) }
  }
}
